import React, { FC } from 'react';
import { StarsRatingBarProps } from '@wix/design-system/dist/types/StarsRatingBar';
import { useTranslation, useHttpClient } from '@wix/fe-essentials-standalone';
import {
  ImageSource,
  ReviewData,
  ReviewListingRequestParams,
  IHandleBIGenericClick,
} from '@common/types';

import ModalContent from '@components/Reviews/ReviewModal/ModalContent';
import TemplateDetails from 'components/Reviews/ReviewModal/TemplateReviewModal/TemplateDetails';
import ToastNotification from 'components/Reviews/ReviewModal/ToastNotification';
import {
  TEMPLATE_POST_REVIEW_API_URL,
  TEMPLATE_UPDATE_REVIEW_API_URL,
} from '@utils/constants';
import {
  ACTIONS,
  useTemplateContext,
} from '@components/Contexts/TemplateContext';
import Long from 'long';

export type TemplateReviewModalProps = {
  id: string;
  templateName: string;
  businessName: string;
  templateMediaSources: ImageSource[];
  onClose?: () => void;
  isMobileView?: boolean;
  onSubmitSuccess: (review: ReviewData) => void;
  enableAutoFocus?: boolean;
  handleBiGenericClick: IHandleBIGenericClick;
  reviewModelEditMode?: boolean;
};

const TemplateReviewModal: FC<TemplateReviewModalProps> = ({
  id,
  templateName,
  businessName,
  templateMediaSources,
  onClose,
  isMobileView = false,
  onSubmitSuccess,
  handleBiGenericClick,
}) => {
  const { dispatch, reviewModalAutoFocus, userReviewForTemplate, reviewScore } =
    useTemplateContext();
  const editMode = !!userReviewForTemplate;
  const BI_EVENT_PREFIX = {
    EDIT: 'Edit your public review modal',
    ADD: 'Leave a public review modal',
  };
  const { t } = useTranslation();
  const httpClient = useHttpClient();

  const writeReview = async (newReview: ReviewListingRequestParams) => {
    const {
      data: { review: review },
    } = await httpClient.post<{ review: ReviewData }>(
      TEMPLATE_POST_REVIEW_API_URL,
      newReview,
    );
    return review;
  };

  const editReview = async (updatedReview: {
    id?: string;
    text: string;
    rating: number;
    revision?: Long;
  }) => {
    const {
      data: { review: review },
    } = await httpClient.post<{ review: ReviewData }>(
      TEMPLATE_UPDATE_REVIEW_API_URL,
      updatedReview,
    );
    return review;
  };

  const biEventPrefix = editMode ? BI_EVENT_PREFIX.EDIT : BI_EVENT_PREFIX.ADD;

  const setRatingScore = (score: StarsRatingBarProps['value']) => {
    dispatch({
      type: ACTIONS.SET_REVIEW_SCORE,
      score: score as StarsRatingBarProps['value'],
    });
  };

  const publish = async (reviewText: string) => {
    handleBiGenericClick({
      clickType: 'Leave a public review modal - Publish',
      clickData: {
        review_rating: reviewScore,
        review_content: reviewText,
      },
    });
    const review = editMode
      ? await editReview({
          id: userReviewForTemplate?.id,
          text: reviewText,
          rating: reviewScore,
          revision: userReviewForTemplate?.revision,
        })
      : await writeReview({
          text: reviewText,
          rating: reviewScore,
          listingId: id,
        });
    if (review) {
      onSubmitSuccess(review as ReviewData);
    } else {
      throw new Error('publish - writeReview failed');
    }
  };

  const TemplateDetailsComponent = (
    <TemplateDetails
      templateName={templateName}
      businessName={businessName}
      templateMediaSources={templateMediaSources}
    />
  );

  function onCloseClick() {
    onClose && onClose();
    handleBiGenericClick({
      clickType: `${biEventPrefix} - Close`,
    });
  }

  function onCancelClick() {
    onClose && onClose();
    handleBiGenericClick({
      clickType: `${biEventPrefix} - cancel`,
    });
  }

  function onRateChange(rate: number) {
    handleBiGenericClick({
      clickType: `${biEventPrefix} - rate`,
      clickData: { review_rating: rate },
    });
  }

  return (
    <>
      <ModalContent
        onClose={onCloseClick}
        onCancel={onCancelClick}
        onRateChange={onRateChange}
        isMobileView={isMobileView}
        publish={publish}
        title={
          editMode
            ? t('marketplace.template-page.edit-review-modal.title')
            : t('marketplace.template-page.review-modal.title')
        }
        mainCtaText={t('marketplace.template-page.review-modal.main-cta')}
        secondaryCtaText={t(
          'marketplace.template-page.review-modal.secondary-cta',
        )}
        errorMsg={t('marketplace.template-page.review-modal.error-message')}
        textAreaLabel={t('marketplace.template-page.review-modal.text-label')}
        TemplateDetailsComponent={TemplateDetailsComponent}
        ratingScore={reviewScore}
        setRatingScore={setRatingScore}
        enableAutoFocus={reviewModalAutoFocus}
        userReview={userReviewForTemplate}
      />
      <ToastNotification isMobileView={isMobileView} />
    </>
  );
};

export default TemplateReviewModal;
